/**
 * SecureEmbed Tier 3 - Real-time Streaming Interface
 *
 * WebSocket streaming, live Recharts, webhook management, demo mode
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Activity, Zap, Search, Play, Pause, Radio, Database,
  Send, Wifi, WifiOff, Sparkles, Clock, Bell, DollarSign, TrendingUp,
  CheckCircle2, AlertCircle, Globe, RefreshCw, Plus,
  Trash2, ToggleLeft, ToggleRight, ExternalLink
} from 'lucide-react';
import {
  LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { searchApi, webhookApi, WebhookSubscription, PriceAlertConfig } from '../services/api';
import { CHART_COLORS, DATASOURCE_COLORS, RECHARTS_THEME } from '../constants/colors';
import TierGate from '../components/TierGate';
import DataSourceBadge from '../components/DataSourceBadge';
import InfoTooltip from '../components/InfoTooltip';

interface StreamEvent {
  id: string;
  timestamp: string;
  type: 'query' | 'result' | 'insight' | 'alert' | 'system';
  source: string;
  content: string;
  confidence?: number;
  latency?: number;
}

interface StreamStats {
  events: number;
  queries: number;
  latencyAvg: number;
  eventsPerMin: Array<{ time: string; count: number }>;
  typeDistribution: Array<{ name: string; value: number }>;
}

interface SearchResult {
  id: string;
  score: number;
  content: string;
  source: string;
  region?: string;
  metadata?: Record<string, any>;
}

const SAMPLE_QUERIES = [
  'electricity demand forecast NSW',
  'renewable energy integration patterns',
  'grid stability during peak hours',
  'price volatility correlation factors',
];

const PRICE_ALERT_EVENTS = [
  { id: 'price.spike', label: 'Price Spike', description: 'Alerts when prices exceed threshold' },
  { id: 'price.negative', label: 'Negative Price', description: 'Alerts on negative wholesale prices' },
  { id: 'anomaly.market', label: 'Market Anomaly', description: 'Statistical deviation from recent patterns' },
];

const NEM_REGIONS = ['NSW1', 'VIC1', 'QLD1', 'SA1', 'TAS1'];

const EVENT_TYPES = ['query', 'result', 'insight', 'alert', 'system'] as const;
const TYPE_COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#6B7280'];

const DEMO_EVENTS: Omit<StreamEvent, 'id' | 'timestamp'>[] = [
  { type: 'system', source: 'aemo', content: 'Connected to AEMO NEM real-time feed — receiving spot price updates' },
  { type: 'insight', source: 'aemo', content: 'Anomaly detected: NSW spot price spike to $342/MWh (3.2σ above 7-day mean)', confidence: 0.94 },
  { type: 'query', source: 'bom', content: 'Semantic search: "temperature extremes correlated with demand"', latency: 23 },
  { type: 'result', source: 'bom', content: 'Found 14 cross-source correlations between BOM temperature data and AEMO demand curves', confidence: 0.87, latency: 45 },
  { type: 'alert', source: 'aer', content: 'AER pricing determination #2026-PD-017 published — flagged for regulatory impact analysis' },
  { type: 'insight', source: 'aemo', content: 'Pattern recognition: Demand ramp-up 15% steeper than seasonal average in VIC region', confidence: 0.91 },
  { type: 'system', source: 'bom', content: 'BOM weather station feed reconnected — 847 stations reporting' },
  { type: 'result', source: 'aemo', content: 'Cluster analysis complete: 5 distinct price regime clusters identified in SA interconnector flows', confidence: 0.88, latency: 67 },
  { type: 'query', source: 'aemo', content: 'Auto-query triggered: "renewable generation forecast next 4h"', latency: 18 },
  { type: 'insight', source: 'bom', content: 'Weather-energy correlation: Temperature forecast >38°C in Adelaide tomorrow — predicted demand uplift 12%', confidence: 0.82 },
  { type: 'alert', source: 'aemo', content: 'Market notice: AEMO intervention event in QLD — constraint equation binding', latency: 12 },
  { type: 'result', source: 'aer', content: 'Regulatory compliance check: 3 new AER determinations require embedding re-indexing', confidence: 0.95, latency: 34 },
];

const Pulse: React.FC<{ color: string; size?: 'sm' | 'md' }> = ({ color, size = 'md' }) => {
  const s = size === 'sm' ? 'w-2 h-2' : 'w-3 h-3';
  return (
    <span className="relative flex">
      <span className={`animate-ping absolute inline-flex ${s} rounded-full opacity-75`} style={{ backgroundColor: color }} />
      <span className={`relative inline-flex ${s} rounded-full`} style={{ backgroundColor: color }} />
    </span>
  );
};

const getEventStyle = (type: StreamEvent['type']) => {
  const styles: Record<string, { bg: string; border: string; text: string; icon: React.ElementType }> = {
    query: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', icon: Search },
    result: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', icon: CheckCircle2 },
    insight: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600', icon: Sparkles },
    alert: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', icon: AlertCircle },
    system: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', icon: Radio },
  };
  return styles[type] || styles.system;
};

// ── Taster content for Tier 2 users (demo mode) ──────────────────────────
const DemoTaster: React.FC = () => {
  const [demoEvents, setDemoEvents] = useState<StreamEvent[]>([]);
  const [demoStats, setDemoStats] = useState<StreamStats>({
    events: 0, queries: 0, latencyAvg: 0,
    eventsPerMin: [], typeDistribution: [],
  });
  const demoIdx = useRef(0);

  useEffect(() => {
    // Replay demo events one by one
    const interval = setInterval(() => {
      const template = DEMO_EVENTS[demoIdx.current % DEMO_EVENTS.length];
      const evt: StreamEvent = {
        ...template,
        id: `demo_${Date.now()}_${demoIdx.current}`,
        timestamp: new Date().toISOString(),
      };
      setDemoEvents(prev => [evt, ...prev].slice(0, 40));

      setDemoStats(prev => {
        const newEvents = prev.events + 1;
        const newQueries = prev.queries + (template.type === 'query' ? 1 : 0);
        const newLatency = template.latency
          ? Math.round((prev.latencyAvg * prev.events + template.latency) / newEvents)
          : prev.latencyAvg;

        const now = new Date();
        const timeLabel = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
        const epm = [...prev.eventsPerMin];
        if (epm.length > 0 && epm[epm.length - 1].time === timeLabel) {
          epm[epm.length - 1].count += 1;
        } else {
          epm.push({ time: timeLabel, count: 1 });
          if (epm.length > 15) epm.shift();
        }

        const dist = EVENT_TYPES.map(t => ({
          name: t,
          value: [evt, ...demoEvents].filter(e => e.type === t).length,
        }));

        return { events: newEvents, queries: newQueries, latencyAvg: newLatency, eventsPerMin: epm, typeDistribution: dist };
      });

      demoIdx.current += 1;
    }, 2500);

    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
        <Activity className="w-5 h-5 text-amber-600 shrink-0" />
        <div>
          <span className="font-medium text-amber-800">Demo Mode</span>
          <span className="text-sm text-amber-700 ml-2">
            Replaying sample events from recent analytics data. Upgrade to Tier 3 for live WebSocket streaming.
          </span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Events Received', value: demoStats.events, color: 'text-purple-600', bg: 'bg-purple-100', icon: Activity },
          { label: 'Queries Processed', value: demoStats.queries, color: 'text-blue-600', bg: 'bg-blue-100', icon: Search },
          { label: 'Avg Latency', value: `${demoStats.latencyAvg}ms`, color: 'text-green-600', bg: 'bg-green-100', icon: Clock },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <Pulse color="#22c55e" size="sm" />
            </div>
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-sm text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Events / Minute</h3>
          {demoStats.eventsPerMin.length > 1 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={demoStats.eventsPerMin}>
                <CartesianGrid {...RECHARTS_THEME.grid} />
                <XAxis dataKey="time" tick={RECHARTS_THEME.label} />
                <YAxis tick={RECHARTS_THEME.label} />
                <Tooltip contentStyle={RECHARTS_THEME.tooltip.contentStyle} />
                <Line type="monotone" dataKey="count" stroke={CHART_COLORS.primary} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">Accumulating data...</div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Event Type Distribution</h3>
          {demoStats.typeDistribution.some(d => d.value > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={demoStats.typeDistribution.filter(d => d.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {demoStats.typeDistribution.filter(d => d.value > 0).map((_, i) => (
                    <Cell key={i} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">Accumulating data...</div>
          )}
        </div>
      </div>

      {/* Demo event stream */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Radio className="w-5 h-5 text-green-500" />
            <span className="font-semibold text-gray-900">Event Stream (Demo)</span>
            <Pulse color="#22c55e" size="sm" />
          </div>
          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">DEMO</span>
        </div>
        <div className="p-4 max-h-[400px] overflow-y-auto space-y-2 bg-gray-50">
          {demoEvents.map(event => {
            const style = getEventStyle(event.type);
            const Icon = style.icon;
            return (
              <div key={event.id} className={`${style.bg} border ${style.border} rounded-xl p-3`}>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${style.bg}`}>
                    <Icon className={`w-4 h-4 ${style.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium ${style.text} uppercase`}>{event.type}</span>
                      <DataSourceBadge source={event.source} size="sm" />
                      {event.latency != null && <span className="text-xs text-gray-500">{event.latency}ms</span>}
                    </div>
                    <p className="text-sm text-gray-700">{event.content}</p>
                    {event.confidence != null && (
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-purple-500 rounded-full" style={{ width: `${event.confidence * 100}%` }} />
                        </div>
                        <span className="text-xs text-gray-500">{(event.confidence * 100).toFixed(0)}%</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {demoEvents.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <div className="w-12 h-12 mx-auto mb-3 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
              <p>Starting demo stream...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Tier 3 Component ────────────────────────────────────────────────
const Tier3Streaming: React.FC = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [events, setEvents] = useState<StreamEvent[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [stats, setStats] = useState<StreamStats>({
    events: 0, queries: 0, latencyAvg: 0,
    eventsPerMin: [], typeDistribution: [],
  });
  const [activeTab, setActiveTab] = useState<'stream' | 'webhooks'>('stream');

  // Webhooks
  const [webhooks, setWebhooks] = useState<WebhookSubscription[]>([]);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [showAddWebhook, setShowAddWebhook] = useState(false);
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [newWebhookEvents, setNewWebhookEvents] = useState<string[]>(['insight', 'alert']);
  const [priceThreshold, setPriceThreshold] = useState(300);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([...NEM_REGIONS]);
  const [enableNegativePrice, setEnableNegativePrice] = useState(true);

  const wsRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<HTMLDivElement>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scroll stream to bottom
  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [events]);

  // Fetch webhooks
  useEffect(() => {
    const fetchWebhooks = async () => {
      setWebhookLoading(true);
      try {
        const res = await webhookApi.list();
        if (res?.success) {
          setWebhooks(res.data.webhooks || []);
        }
      } catch {
        // Keep empty
      } finally {
        setWebhookLoading(false);
      }
    };
    fetchWebhooks();
  }, []);

  const getWsEndpoint = (): string | null => {
    const config = (window as any).SECUREEMBED_CONFIG;
    return config?.WS_ENDPOINT || null;
  };

  const addEvent = useCallback((event: StreamEvent) => {
    setEvents(prev => [...prev, event].slice(-200));
    setStats(prev => {
      const newEvents = prev.events + 1;
      const newQueries = prev.queries + (event.type === 'query' ? 1 : 0);
      const newLatency = event.latency
        ? Math.round((prev.latencyAvg * prev.events + event.latency) / newEvents)
        : prev.latencyAvg;

      const now = new Date();
      const timeLabel = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
      const epm = [...prev.eventsPerMin];
      if (epm.length > 0 && epm[epm.length - 1].time === timeLabel) {
        epm[epm.length - 1].count += 1;
      } else {
        epm.push({ time: timeLabel, count: 1 });
        if (epm.length > 20) epm.shift();
      }

      const counts: Record<string, number> = {};
      [...prev.typeDistribution].forEach(d => { counts[d.name] = d.value; });
      counts[event.type] = (counts[event.type] || 0) + 1;
      const dist = EVENT_TYPES.map(t => ({ name: t, value: counts[t] || 0 }));

      return { events: newEvents, queries: newQueries, latencyAvg: newLatency, eventsPerMin: epm, typeDistribution: dist };
    });
  }, []);

  const connect = useCallback(() => {
    const wsUrl = getWsEndpoint();
    if (!wsUrl) {
      // No WebSocket endpoint — fall back to demo mode using analytics data
      setIsConnected(true);
      addEvent({
        id: `sys_${Date.now()}`,
        timestamp: new Date().toISOString(),
        type: 'system',
        source: 'portal',
        content: 'WebSocket endpoint not configured. Running in live demo mode with analytics data.',
      });

      // Replay demo events from analytics
      let idx = 0;
      const interval = setInterval(() => {
        const template = DEMO_EVENTS[idx % DEMO_EVENTS.length];
        addEvent({
          ...template,
          id: `live_${Date.now()}_${idx}`,
          timestamp: new Date().toISOString(),
        });
        idx++;
      }, 3000);

      // Store interval reference for cleanup
      reconnectRef.current = interval as unknown as ReturnType<typeof setTimeout>;
      return;
    }

    const token = localStorage.getItem('authToken');
    const ws = new WebSocket(`${wsUrl}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      addEvent({
        id: `sys_${Date.now()}`,
        timestamp: new Date().toISOString(),
        type: 'system',
        source: 'portal',
        content: 'Connected to SecureEmbed streaming endpoint.',
      });
    };

    ws.onmessage = (msg) => {
      try {
        const data = JSON.parse(msg.data);
        addEvent({
          id: data.id || `evt_${Date.now()}`,
          timestamp: data.timestamp || new Date().toISOString(),
          type: data.type || 'system',
          source: data.source || 'unknown',
          content: data.content || data.message || JSON.stringify(data),
          confidence: data.confidence,
          latency: data.latency,
        });
      } catch {
        addEvent({
          id: `raw_${Date.now()}`,
          timestamp: new Date().toISOString(),
          type: 'system',
          source: 'ws',
          content: msg.data,
        });
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      addEvent({
        id: `sys_${Date.now()}`,
        timestamp: new Date().toISOString(),
        type: 'system',
        source: 'portal',
        content: 'Disconnected from streaming endpoint.',
      });
    };

    ws.onerror = () => {
      addEvent({
        id: `err_${Date.now()}`,
        timestamp: new Date().toISOString(),
        type: 'alert',
        source: 'portal',
        content: 'WebSocket connection error. Will attempt reconnection.',
      });
    };
  }, [addEvent]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectRef.current) {
      clearInterval(reconnectRef.current as unknown as ReturnType<typeof setInterval>);
      reconnectRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearInterval(reconnectRef.current as unknown as ReturnType<typeof setInterval>);
    };
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    setSearchResults([]);
    try {
      const res = await searchApi.similar(searchQuery, { limit: 10 });
      if (res?.success) {
        setSearchResults(res.data.results.map(r => ({
          id: r.id,
          score: r.score,
          content: r.content,
          source: r.source,
          region: r.region,
          metadata: r.metadata,
        })));
        addEvent({
          id: `q_${Date.now()}`,
          timestamp: new Date().toISOString(),
          type: 'query',
          source: 'portal',
          content: `Search: "${searchQuery}" — ${res.data.total} results (${res.data.query_time_ms || 0}ms)`,
          latency: res.data.query_time_ms,
        });
      }
    } catch {
      // Keep empty results
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddWebhook = async () => {
    if (!newWebhookUrl.trim()) return;
    try {
      const hasPriceAlerts = newWebhookEvents.some(e => e.startsWith('price.') || e.startsWith('anomaly.'));
      const priceAlertConfig: PriceAlertConfig | undefined = hasPriceAlerts ? {
        threshold_mwh: priceThreshold,
        regions: selectedRegions,
        enable_negative_price: enableNegativePrice,
      } : undefined;
      await webhookApi.create({ url: newWebhookUrl, events: newWebhookEvents, price_alert_config: priceAlertConfig });
      const res = await webhookApi.list();
      if (res?.success) setWebhooks(res.data.webhooks || []);
      setNewWebhookUrl('');
      setShowAddWebhook(false);
    } catch {
      // Error handled silently
    }
  };

  const handleDeleteWebhook = async (id: string) => {
    try {
      await webhookApi.delete(id);
      setWebhooks(prev => prev.filter(w => w.id !== id));
    } catch {
      // Error handled silently
    }
  };

  const handleToggleWebhook = async (id: string, active: boolean) => {
    try {
      await webhookApi.toggle(id, active);
      setWebhooks(prev => prev.map(w => w.id === id ? { ...w, active } : w));
    } catch {
      // Error handled silently
    }
  };

  return (
    <TierGate
      requiredTier={3}
      tasterContent={<DemoTaster />}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <Radio className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Real-time Stream</h1>
                <p className="text-sm text-gray-500">Live embedding events, search &amp; webhooks</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${isConnected ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                {isConnected ? <Wifi className="w-4 h-4 text-green-600" /> : <WifiOff className="w-4 h-4 text-gray-400" />}
                <span className={`text-sm font-medium ${isConnected ? 'text-green-600' : 'text-gray-500'}`}>
                  {isConnected ? 'Connected' : 'Disconnected'}
                </span>
                {isConnected && <Pulse color="#22c55e" size="sm" />}
              </div>

              <button
                onClick={isConnected ? disconnect : connect}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold transition-all ${
                  isConnected
                    ? 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
                    : 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg hover:from-purple-600 hover:to-indigo-700'
                }`}
              >
                {isConnected ? <><Pause className="w-4 h-4" />Disconnect</> : <><Play className="w-4 h-4" />Connect</>}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Live Events', value: stats.events, icon: Activity, color: 'text-purple-600', bg: 'bg-purple-100', tipId: 'streaming.stat.live-events' },
            { label: 'Queries', value: stats.queries, icon: Search, color: 'text-blue-600', bg: 'bg-blue-100', tipId: 'streaming.stat.queries' },
            { label: 'Avg Latency', value: `${stats.latencyAvg}ms`, icon: Clock, color: 'text-green-600', bg: 'bg-green-100', tipId: 'streaming.stat.avg-latency' },
            { label: 'Sources', value: 3, icon: Database, color: 'text-amber-600', bg: 'bg-amber-100', tipId: 'streaming.stat.sources' },
          ].map(stat => (
            <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                {stat.label === 'Live Events' && isConnected && <Pulse color="#9333ea" />}
              </div>
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-sm text-gray-500 flex items-center gap-1">{stat.label} <InfoTooltip tooltipId={stat.tipId} /></div>
            </div>
          ))}
        </div>

        {/* Live Charts */}
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1">Events / Minute <InfoTooltip tooltipId="streaming.chart.events-per-min" /></h3>
            {stats.eventsPerMin.length > 1 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={stats.eventsPerMin}>
                  <CartesianGrid {...RECHARTS_THEME.grid} />
                  <XAxis dataKey="time" tick={RECHARTS_THEME.label} />
                  <YAxis tick={RECHARTS_THEME.label} />
                  <Tooltip contentStyle={RECHARTS_THEME.tooltip.contentStyle} />
                  <Line type="monotone" dataKey="count" stroke={CHART_COLORS.primary} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
                {isConnected ? 'Accumulating data...' : 'Connect to see live chart'}
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1">Event Type Distribution <InfoTooltip tooltipId="streaming.chart.type-distribution" /></h3>
            {stats.typeDistribution.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={stats.typeDistribution.filter(d => d.value > 0)}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={75}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {stats.typeDistribution.filter(d => d.value > 0).map((entry) => (
                      <Cell key={entry.name} fill={TYPE_COLORS[EVENT_TYPES.indexOf(entry.name as typeof EVENT_TYPES[number]) % TYPE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
                {isConnected ? 'Accumulating data...' : 'Connect to see live chart'}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
          {(['stream', 'webhooks'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'stream' ? 'Stream & Search' : 'Webhooks'}
            </button>
          ))}
        </div>

        {activeTab === 'stream' && (
          <div className="grid grid-cols-2 gap-6">
            {/* Search Panel */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-900">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  Live Semantic Search
                </h2>
                <p className="text-sm text-gray-400 mt-1">Real-time search across all data sources</p>
              </div>
              <div className="p-4">
                <div className="relative mb-4">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Enter your search query..."
                    className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400
                             focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 pr-12"
                  />
                  <button
                    onClick={handleSearch}
                    disabled={isSearching || !searchQuery.trim()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white
                             rounded-lg hover:opacity-90 disabled:opacity-50 transition-all"
                  >
                    {isSearching ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {SAMPLE_QUERIES.map(query => (
                    <button
                      key={query}
                      onClick={() => { setSearchQuery(query); }}
                      className="px-3 py-1.5 text-xs rounded-full border bg-purple-100 text-purple-600 border-purple-200 hover:bg-purple-200 transition-colors"
                    >
                      {query}
                    </button>
                  ))}
                </div>

                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {searchResults.length === 0 && !isSearching && (
                    <div className="text-center py-12 text-gray-400">
                      <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Enter a query to search across data sources</p>
                    </div>
                  )}
                  {isSearching && (
                    <div className="text-center py-12">
                      <div className="w-12 h-12 mx-auto mb-3 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
                      <p className="text-purple-600">Searching embeddings...</p>
                    </div>
                  )}
                  {searchResults.map(result => (
                    <div key={result.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200 hover:border-gray-300 transition-all">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded-full font-medium">
                            {(result.score * 100).toFixed(0)}% match
                          </span>
                          <DataSourceBadge source={result.source} size="sm" />
                        </div>
                        {result.region && <span className="text-xs text-gray-400">{result.region}</span>}
                      </div>
                      <p className="text-sm text-gray-700">{result.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Live Stream Panel */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-900">
                    <Radio className="w-5 h-5 text-green-500" />
                    Live Event Stream
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">Real-time insights and alerts</p>
                </div>
                {isConnected && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full border border-green-200">
                    <Pulse color="#22c55e" size="sm" />
                    <span className="text-xs text-green-600 font-medium">LIVE</span>
                  </div>
                )}
              </div>

              <div ref={streamRef} className="p-4 h-[500px] overflow-y-auto space-y-2 bg-gray-50">
                {!isConnected && events.length === 0 && (
                  <div className="text-center py-16 text-gray-400">
                    <WifiOff className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium text-gray-500">Not Connected</p>
                    <p className="text-sm mt-1">Click "Connect" to start receiving live events</p>
                  </div>
                )}
                {isConnected && events.length === 0 && (
                  <div className="text-center py-16 text-gray-500">
                    <div className="w-16 h-16 mx-auto mb-4 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
                    <p>Waiting for events...</p>
                  </div>
                )}
                {events.map(event => {
                  const style = getEventStyle(event.type);
                  const Icon = style.icon;
                  return (
                    <div key={event.id} className={`${style.bg} border ${style.border} rounded-xl p-3`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${style.bg}`}>
                          <Icon className={`w-4 h-4 ${style.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-medium ${style.text} uppercase`}>{event.type}</span>
                            <DataSourceBadge source={event.source} size="sm" />
                            {event.latency != null && <span className="text-xs text-gray-500">{event.latency}ms</span>}
                            <span className="text-xs text-gray-400 ml-auto">
                              {new Date(event.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700">{event.content}</p>
                          {event.confidence != null && (
                            <div className="flex items-center gap-2 mt-2">
                              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div className="h-full bg-purple-500 rounded-full" style={{ width: `${event.confidence * 100}%` }} />
                              </div>
                              <span className="text-xs text-gray-500">{(event.confidence * 100).toFixed(0)}%</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'webhooks' && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-gray-600" />
                <span className="font-semibold text-gray-900">Webhook Subscriptions</span>
                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{webhooks.length}</span>
              </div>
              <button
                onClick={() => setShowAddWebhook(!showAddWebhook)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[#2B064F] text-white rounded-lg hover:bg-[#3d0a6e] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Webhook
              </button>
            </div>

            {showAddWebhook && (
              <div className="p-4 border-b border-gray-100 bg-gray-50">
                <div className="flex gap-3">
                  <input
                    type="url"
                    value={newWebhookUrl}
                    onChange={(e) => setNewWebhookUrl(e.target.value)}
                    placeholder="https://your-endpoint.com/webhook"
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                  <button
                    onClick={handleAddWebhook}
                    disabled={!newWebhookUrl.trim()}
                    className="px-4 py-2 bg-[#2B064F] text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-[#3d0a6e] transition-colors"
                  >
                    Create
                  </button>
                </div>
                <div className="mt-3 space-y-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1.5">Stream Events</p>
                    <div className="flex gap-2">
                      {EVENT_TYPES.filter(t => t !== 'system').map(t => (
                        <label key={t} className="flex items-center gap-1.5 text-sm text-gray-600">
                          <input
                            type="checkbox"
                            checked={newWebhookEvents.includes(t)}
                            onChange={(e) => {
                              setNewWebhookEvents(prev =>
                                e.target.checked ? [...prev, t] : prev.filter(x => x !== t)
                              );
                            }}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          {t}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-1">
                      <DollarSign className="w-3 h-3" /> Price Alert Events
                    </p>
                    <div className="flex gap-2">
                      {PRICE_ALERT_EVENTS.map(pa => (
                        <label key={pa.id} className="flex items-center gap-1.5 text-sm text-gray-600" title={pa.description}>
                          <input
                            type="checkbox"
                            checked={newWebhookEvents.includes(pa.id)}
                            onChange={(e) => {
                              setNewWebhookEvents(prev =>
                                e.target.checked ? [...prev, pa.id] : prev.filter(x => x !== pa.id)
                              );
                            }}
                            className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                          />
                          {pa.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  {newWebhookEvents.some(e => e.startsWith('price.') || e.startsWith('anomaly.')) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-medium text-amber-700 flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> Price Alert Configuration
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-600">Spike Threshold ($/MWh)</label>
                          <input
                            type="number"
                            value={priceThreshold}
                            onChange={(e) => setPriceThreshold(Number(e.target.value))}
                            className="w-full mt-1 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">Negative Price Alerts</label>
                          <button
                            onClick={() => setEnableNegativePrice(!enableNegativePrice)}
                            className={`mt-1 flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium ${
                              enableNegativePrice ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {enableNegativePrice ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                            {enableNegativePrice ? 'Enabled' : 'Disabled'}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-600">Regions</label>
                        <div className="flex gap-1.5 mt-1">
                          {NEM_REGIONS.map(r => (
                            <button
                              key={r}
                              onClick={() => setSelectedRegions(prev =>
                                prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]
                              )}
                              className={`px-2 py-0.5 text-xs rounded ${
                                selectedRegions.includes(r) ? 'bg-amber-200 text-amber-800' : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {r}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="divide-y divide-gray-100">
              {webhookLoading && (
                <div className="p-8 text-center text-gray-400">
                  <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
                  <p className="text-sm">Loading webhooks...</p>
                </div>
              )}
              {!webhookLoading && webhooks.length === 0 && (
                <div className="p-8 text-center text-gray-400">
                  <Bell className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No webhook subscriptions configured</p>
                  <p className="text-xs text-gray-400 mt-1">Add a webhook to receive real-time event notifications</p>
                </div>
              )}
              {webhooks.map(wh => (
                <div key={wh.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <button
                      onClick={() => handleToggleWebhook(wh.id, !wh.active)}
                      title={wh.active ? 'Disable' : 'Enable'}
                    >
                      {wh.active ? (
                        <ToggleRight className="w-6 h-6 text-green-500" />
                      ) : (
                        <ToggleLeft className="w-6 h-6 text-gray-400" />
                      )}
                    </button>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span className="text-sm font-mono text-gray-700 truncate">{wh.url}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {wh.events.map(ev => (
                          <span key={ev} className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">{ev}</span>
                        ))}
                        <span className="text-xs text-gray-400">
                          Created {new Date(wh.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteWebhook(wh.id)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Data Sources Footer */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-600 mb-3">Connected Data Sources</h3>
          <div className="grid grid-cols-3 gap-4">
            {[
              { source: 'aemo', label: 'AEMO NEM Data', icon: Zap, events: '1.2k/hr' },
              { source: 'bom', label: 'BOM Weather', icon: Globe, events: '850/hr' },
              { source: 'aer', label: 'AER Regulatory', icon: Database, events: '120/hr' },
            ].map(src => {
              const ds = DATASOURCE_COLORS[src.source];
              return (
              <div key={src.source} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ds?.bg || 'bg-gray-100'}`}>
                  <src.icon className="w-5 h-5" style={{ color: ds?.hex || '#6B7280' }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{src.label}</span>
                    {isConnected && <Pulse color="#22c55e" size="sm" />}
                  </div>
                  <span className="text-xs text-gray-500">{src.events}</span>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </TierGate>
  );
};

export default Tier3Streaming;
